import { useCallback, useEffect, useState } from 'react';
import { useServices } from '@/app/providers';
import { getRoleLabel as getSystemRoleLabel, useI18n } from '@/shared/i18n';
import type { Role } from '@/shared/types';

/**
 * Resuelve el nombre a mostrar de un rol: traducido si es del sistema,
 * o el `displayName`/`name` del rol personalizado (Admin > Roles) si aplica.
 * `tenantRole` solo guarda el código del rol (p. ej. "RECEPCION"), no su
 * nombre visible, así que hay que cruzarlo con roleRepo.getRoles().
 */
export function useRoleLabel() {
  const { roleRepo } = useServices();
  const { t } = useI18n();
  const [customRoles, setCustomRoles] = useState<Role[]>([]);

  useEffect(() => {
    let active = true;
    roleRepo.getRoles()
      .then((roles) => { if (active) setCustomRoles(roles); })
      .catch(() => {});
    return () => { active = false; };
  }, [roleRepo]);

  const getLabel = useCallback((role: string | undefined | null): string => {
    if (!role) return '';
    // Colapsa espacios/guiones a "_" al comparar: un rol "Jefe de Mantenimiento" debe
    // matchear el tenantRole aunque el backend lo haya guardado en SNAKE_CASE.
    const normalize = (value: string) => value.trim().toUpperCase().replace(/[\s-]+/g, '_');
    const normalizedRole = normalize(role);
    const custom = customRoles.find((r) => normalize(r.name) === normalizedRole);
    if (custom) return custom.displayName || custom.name;
    return getSystemRoleLabel(role, t);
  }, [customRoles, t]);

  return { getLabel };
}
